import React, { Fragment, useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import { AlertCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"

import CardSection from "./CardSection"
import MetricLegend from "./MetricLegend"
import SyncStatus from "./SyncStatus"
import {
  MetricValue,
  QuarterlyMetric,
  QuarterlyMetricClient,
  QuarterlyMetricsSummary,
  useQuarterlyMetrics,
} from "./hooks/useQuarterlyMetrics"

interface Props {
  username: string
  year: string
  quarter: number
  display: { QuarterlyPerformance: boolean }
  // MyProviderData shows the sync times under its quarter picker instead
  showSyncStatus?: boolean
}

// -----------------------------
// Formatting
// -----------------------------

const formatValue = (metric: QuarterlyMetric, value: MetricValue | null | undefined) => {
  if (value?.value == null) return "N/A"
  return metric.unit === "Days"
    ? value.value.toFixed(1)
    : `${Math.round(value.value)}%`
}

// "6 of 7 BIPs on time" style detail for hovering a value
const describeValue = (metric: QuarterlyMetric, value: MetricValue | null | undefined) => {
  if (!value || value.value == null) return "Nothing to measure for this period"
  if (metric.unit === "Days") return `${value.denominator} entries`
  if (metric.targetRule === "AllItems" && value.itemCount != null)
    return `${value.itemsMeetingTarget ?? 0} of ${value.itemCount} meeting target`
  return `${value.numerator ?? 0} of ${value.denominator}`
}

// AllItems metrics (Oversight, BIP) are judged item by item, so the value alone can look on target
// while the metric isn't (13% overall, but 1 of 7 cases within 10% - 20%): show the count with it.
// With a single item (a client's own row) the value is that item, so no count is needed.
const isPerItem = (metric: QuarterlyMetric, value: MetricValue | null | undefined) =>
  metric.targetRule === "AllItems" && value?.value != null && value.itemCount != null && value.itemCount > 1

// A range target judged item by item (Oversight: every client within 10% - 20%). Its overall %
// decides nothing and reads like an average, so only the count is shown (PM 2026-10-22, option 3),
// and missing it can mean too high as well as too low ("Not on Target", not "Below Target").
const isRangePerItem = (metric: QuarterlyMetric) =>
  metric.targetRule === "AllItems" && metric.targetMin != null && metric.targetMax != null

// "3 of 10" in place of the value; a client's own row (one item) keeps that client's %
const showsCountOnly = (metric: QuarterlyMetric, value: MetricValue | null | undefined) =>
  isRangePerItem(metric) && isPerItem(metric, value)

// one line of the Needs Attention box
const attentionText = (metric: QuarterlyMetric) => {
  const v = metric.quarterValue
  if (showsCountOnly(metric, v))
    return `${metric.name} is not on target this quarter: ${v!.itemsMeetingTarget ?? 0} of ${
      v!.itemCount
    } within ${metric.targetLabel}.`
  if (isPerItem(metric, v))
    return `${metric.name} is not on target this quarter: ${v!.itemsMeetingTarget ?? 0} of ${
      v!.itemCount
    } within ${metric.targetLabel} (overall ${formatValue(metric, v)}).`
  if (isRangePerItem(metric))
    return `${metric.name} is not on target this quarter (${formatValue(metric, v)} / ${metric.targetLabel}).`
  return `${metric.name} is below target this quarter (${formatValue(metric, v)} / ${metric.targetLabel}).`
}

const monthLabel = (month: string) => format(parseISO(month), "MMM yyyy")

// months that have not started yet show a dash instead of N/A
const isFutureMonth = (month: string) => parseISO(month) > new Date()

// "Smith, Jane" -> "J.S.", "Jane Smith" -> "J.S."
const clientInitials = (client: QuarterlyMetricClient) => {
  const name = client.clientName?.trim()
  if (!name) return `#${client.clientId}`
  const parts = name.includes(",")
    ? [name.split(",")[1], name.split(",")[0]]
    : name.split(/\s+/)
  const letters = parts
    .map((p) => p?.trim()?.[0])
    .filter(Boolean)
    .slice(0, 2)
  return letters.length ? `${letters.join(".")}.`.toUpperCase() : `#${client.clientId}`
}

// -----------------------------
// Small pieces
// -----------------------------

const StatusDot: React.FC<{ meets: boolean | null | undefined }> = ({ meets }) => (
  <span
    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
      meets == null ? "bg-gray-400" : meets ? "bg-green-600" : "bg-red-600"
    }`}
  />
)

const ValueCell: React.FC<{
  metric: QuarterlyMetric
  value: MetricValue | null | undefined
  month?: string
}> = ({ metric, value, month }) => {
  if (metric.isComingSoon) return <span className="text-muted-foreground">—</span>
  // the proc writes empty rows (nothing to measure) for months not started yet as well
  if (month && value?.value == null && isFutureMonth(month))
    return <span className="text-muted-foreground">—</span>

  return (
    <span className="inline-flex items-center gap-2" title={describeValue(metric, value)}>
      <StatusDot meets={value?.meetsTarget} />
      {showsCountOnly(metric, value) ? (
        <span>
          {value!.itemsMeetingTarget ?? 0} of {value!.itemCount}
        </span>
      ) : (
        <span className={value?.value == null ? "text-muted-foreground" : ""}>
          {formatValue(metric, value)}
        </span>
      )}
      {isPerItem(metric, value) && !showsCountOnly(metric, value) && (
        <span className="text-muted-foreground">
          ({value!.itemsMeetingTarget ?? 0}/{value!.itemCount})
        </span>
      )}
    </span>
  )
}

const StatusBadge: React.FC<{
  meets: boolean | null | undefined
  comingSoon?: boolean
  range?: boolean // range targets miss by being too high as well: "Not on Target"
}> = ({ meets, comingSoon, range }) => {
  if (comingSoon) return <Badge variant="secondary">Coming Soon</Badge>
  if (meets == null) return <Badge variant="outline">N/A</Badge>
  return meets ? (
    <Badge className="border-transparent bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100">
      On Target
    </Badge>
  ) : (
    <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-100">
      {range ? "Not on Target" : "Below Target"}
    </Badge>
  )
}

// Quarterly performance score (HR 2026-10-15): every metric weighs the same and N/A metrics drop
// out, so score = met / applicable. Calculated by EPR_GenerateMetrics; nothing is worked out here.
const ScoreCard: React.FC<{ summary: QuarterlyMetricsSummary }> = ({ summary }) => {
  const { score, requiredScore, meetsRequirement, metricsTotal, metricsApplicable, metricsMet } =
    summary
  const notApplicable = metricsTotal - metricsApplicable

  return (
    <div className="mt-3 w-full max-w-xl rounded-md border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold">Quarterly Performance Score</div>
        {meetsRequirement != null &&
          (meetsRequirement ? (
            <Badge className="border-transparent bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100">
              Meets Requirement
            </Badge>
          ) : (
            <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-100">
              Below Requirement
            </Badge>
          ))}
      </div>

      {score == null ? (
        <div className="mt-3 text-sm text-muted-foreground">
          {metricsApplicable === 0
            ? "No metrics had anything to measure this quarter."
            : "The score has not been calculated for this quarter yet."}
        </div>
      ) : (
        <>
          <div className="mt-2 text-center">
            <div
              className={`text-3xl font-bold ${
                meetsRequirement ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
              }`}
            >
              {Number(score).toFixed(1)}%
            </div>
            <div className="text-sm text-muted-foreground">Reweighted Performance Score</div>
          </div>

          {/* bar with a tick at the pass mark */}
          <div className="relative mt-3 h-2.5 w-full rounded-full bg-muted">
            <div
              className={`h-full rounded-full ${meetsRequirement ? "bg-green-600" : "bg-amber-500"}`}
              style={{ width: `${Math.min(100, Math.max(0, Number(score)))}%` }}
            />
            {requiredScore != null && (
              <div
                className="absolute -top-1 w-0.5 bg-foreground"
                style={{ left: `${requiredScore}%`, height: "1.125rem" }}
                title={`${requiredScore}% required`}
              />
            )}
          </div>
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>0%</span>
            {requiredScore != null && <span>{Number(requiredScore).toFixed(0)}% required</span>}
            <span>100%</span>
          </div>
        </>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="text-muted-foreground">Applicable metrics</div>
        <div className="text-right font-semibold">
          {metricsApplicable} of {metricsTotal}
        </div>
        <div className="text-muted-foreground">Metrics met</div>
        <div className="text-right font-semibold">
          {metricsMet} of {metricsApplicable}
        </div>
      </div>

      {notApplicable > 0 && metricsApplicable > 0 && (
        <div className="mt-2 text-xs text-muted-foreground">
          {notApplicable} metric(s) had nothing to measure (N/A) and are left out. Each applicable
          metric carries {(100 / metricsApplicable).toFixed(2)}% of the score.
        </div>
      )}
    </div>
  )
}

// -----------------------------
// Section
// -----------------------------

const QuarterlyPerformanceSection: React.FC<Props> = ({
  username,
  year,
  quarter,
  display,
  showSyncStatus = true,
}) => {
  const { data, loading, error } = useQuarterlyMetrics(
    username,
    year,
    quarter,
    display.QuarterlyPerformance
  )

  const [clientFilter, setClientFilter] = useState<"all" | "below">("all")
  const [expandedClient, setExpandedClient] = useState<string | null>(null)

  const scoredMetrics = useMemo(
    () => (data?.metrics ?? []).filter((m) => !m.isComingSoon),
    [data]
  )

  const belowTarget = useMemo(
    () => scoredMetrics.filter((m) => m.quarterValue?.meetsTarget === false),
    [scoredMetrics]
  )

  const clientsBelow = useMemo(
    () => (data?.clients ?? []).filter((c) => !c.meetsAllTargets),
    [data]
  )

  if (!display.QuarterlyPerformance) return null

  if (loading) {
    return (
      <CardSection title="Quarterly Performance">
        <div className="col-span-12 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading Quarterly Performance...</span>
        </div>
      </CardSection>
    )
  }

  if (error || !data) {
    return (
      <CardSection title="Quarterly Performance">
        <div className="col-span-12 text-sm text-muted-foreground">
          {error ?? "No quarterly performance data."}
        </div>
      </CardSection>
    )
  }

  const visibleClients = clientFilter === "below" ? clientsBelow : data.clients
  const summary = data.summary

  return (
    <CardSection title="Quarterly Performance">
      {/* Header */}
      <div className="col-span-12 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-sm text-muted-foreground">
            {format(parseISO(data.qStart), "MMMM")} – {format(parseISO(data.qEnd), "MMMM yyyy")}
          </div>

          {summary ? (
            <ScoreCard summary={summary} />
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">
              Metrics have not been calculated for this quarter yet.
            </div>
          )}

          {showSyncStatus && (
            <SyncStatus
              className="mt-2"
              eprSyncedAt={data.eprSyncedAt}
              metricsCalculatedAt={data.calculatedAt}
              metricsBehindEpr={data.metricsBehindEpr}
            />
          )}
        </div>

        {belowTarget.length > 0 && (
          <div className="flex gap-3 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-950 dark:text-red-100 lg:max-w-xl">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <div className="font-semibold">Needs Attention</div>
              {belowTarget.map((m) => (
                <div key={m.key} className="text-sm">
                  {attentionText(m)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      <div className="col-span-12">
        <h3 className="mb-2 font-semibold">Performance Metrics</h3>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead>Target</TableHead>
                {data.months.map((m) => (
                  <TableHead key={m}>{monthLabel(m)}</TableHead>
                ))}
                <TableHead>Quarter</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.metrics.map((metric) => (
                <TableRow key={metric.key} title={metric.description ?? undefined}>
                  <TableCell className="font-medium">{metric.name}</TableCell>
                  <TableCell>{metric.targetLabel}</TableCell>
                  {data.months.map((month, i) => (
                    <TableCell key={month}>
                      <ValueCell metric={metric} value={metric.monthValues[i]} month={month} />
                    </TableCell>
                  ))}
                  <TableCell>
                    <ValueCell metric={metric} value={metric.quarterValue} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      meets={metric.quarterValue?.meetsTarget}
                      comingSoon={metric.isComingSoon}
                      range={isRangePerItem(metric)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* What each metric means - text from EPR_MetricDefinition.Explanation */}
      <div className="col-span-12">
        <MetricLegend metrics={data.metrics} />
      </div>

      {/* Client Summary */}
      <div className="col-span-12">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">Client Summary</h3>
          <Tabs value={clientFilter} onValueChange={(v) => setClientFilter(v as "all" | "below")}>
            <TabsList>
              <TabsTrigger value="all">All Clients ({data.clients.length})</TabsTrigger>
              <TabsTrigger value="below">Clients Below Target ({clientsBelow.length})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {visibleClients.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {clientFilter === "below" ? "No clients below target." : "No clients this quarter."}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  {scoredMetrics.map((m) => (
                    <TableHead key={m.key}>{m.name}</TableHead>
                  ))}
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleClients.map((client) => {
                  const expanded = expandedClient === client.clientId
                  const hasValues = scoredMetrics.some(
                    (m) => client.metrics[m.key]?.quarterValue?.value != null
                  )

                  return (
                    <Fragment key={client.clientId}>
                      <TableRow>
                        <TableCell className="font-medium">{clientInitials(client)}</TableCell>
                        {scoredMetrics.map((m) => (
                          <TableCell key={m.key}>
                            <ValueCell metric={m} value={client.metrics[m.key]?.quarterValue} />
                          </TableCell>
                        ))}
                        <TableCell>
                          <StatusBadge meets={hasValues ? client.meetsAllTargets : null} />
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline dark:text-blue-300"
                            onClick={() => setExpandedClient(expanded ? null : client.clientId)}
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            View
                          </button>
                        </TableCell>
                      </TableRow>

                      {/* By month for this client */}
                      {expanded && (
                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                          <TableCell colSpan={scoredMetrics.length + 3}>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Metric</TableHead>
                                  {data.months.map((m) => (
                                    <TableHead key={m}>{monthLabel(m)}</TableHead>
                                  ))}
                                  <TableHead>Quarter</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {scoredMetrics.map((m) => (
                                  <TableRow key={m.key}>
                                    <TableCell>{m.name}</TableCell>
                                    {data.months.map((month, i) => (
                                      <TableCell key={month}>
                                        <ValueCell
                                          metric={m}
                                          value={client.metrics[m.key]?.monthValues[i]}
                                          month={month}
                                        />
                                      </TableCell>
                                    ))}
                                    <TableCell>
                                      <ValueCell
                                        metric={m}
                                        value={client.metrics[m.key]?.quarterValue}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </CardSection>
  )
}

export default QuarterlyPerformanceSection
